import { useCallback } from "react";
import { Button, Tooltip, message } from "antd";
import { EasyAvatar } from "fin-web-components";
import { avatarDisplayWord } from "../../../utils/avatarDisplayWord";
import { SPACE_HOME_HERO_LAYOUT } from "../Config";
import type { SpaceProfile } from "../SpaceType";
import { formatSpaceMetricValue } from "../spacePageUtils";
import { SpaceCertLabelTag } from "../components/SpaceCertLabelTag";

/** 空间信息 + 关注/订阅按钮 */
export function SpaceHomeInfoSection(props: {
  profile: SpaceProfile;
  isLoggedIn: boolean;
  favorStatus: 0 | 1;
  onLoginRequest: () => void;
  onFollow: () => void;
  onRequestUnfollow: () => void;
  onSubscribe: () => void;
}): JSX.Element {
  const {
    profile,
    isLoggedIn,
    favorStatus,
    onLoginRequest,
    onFollow,
    onRequestUnfollow,
    onSubscribe,
  } = props;
  const certLabel = profile.certLabelName?.trim() ?? "";
  const showCertLabel = certLabel !== "";
  const introText = profile.signature?.trim() ?? "";
  const { avatar, followButton, subscribeButton } = SPACE_HOME_HERO_LAYOUT;

  const onCopySpaceId = useCallback(() => {
    void navigator.clipboard.writeText(profile.spaceId).then(
      () => {
        message.success("已复制空间ID");
      },
      () => {
        message.error("复制失败，请重试");
      }
    );
  }, [profile.spaceId]);

  return (
    <div className="space-home-hero">
      <div className="space-home-avatar-wrap">
        <EasyAvatar
          shape="circle"
          size={avatar.size}
          url={profile.avatarUrl?.trim() || undefined}
          word={avatarDisplayWord(profile.name, profile.spaceId)}
          alt=""
          className="space-home-avatar"
          border={{ width: avatar.border.width, color: avatar.border.color }}
        />
      </div>
      <div className="space-home-titles">
        <div className="space-home-row space-home-row--name">
          <span className="space-home-name-text">{profile.name}</span>
          {showCertLabel ? <SpaceCertLabelTag name={certLabel} /> : null}
        </div>
        {introText !== "" ? (
          <p className="space-home-intro">{introText}</p>
        ) : null}
        <div className="space-home-metrics" aria-label="空间数据">
          <div className="space-home-metrics-col">
            <div className="space-home-metrics-value-row">
              <span className="space-home-metrics-value">
                {formatSpaceMetricValue(profile.followerCount)}
              </span>
            </div>
            <span className="space-home-metrics-label">粉丝</span>
          </div>
          <div className="space-home-metrics-col">
            <div className="space-home-metrics-value-row">
              <Tooltip title="复制">
                <span
                  className="space-home-metrics-value space-home-metrics-value--copyable"
                  translate="no"
                  role="button"
                  tabIndex={0}
                  onClick={onCopySpaceId}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      onCopySpaceId();
                    }
                  }}
                >
                  {profile.spaceId}
                </span>
              </Tooltip>
            </div>
            <span className="space-home-metrics-label">龙粉号</span>
          </div>
          <div className="space-home-metrics-col">
            <div className="space-home-metrics-value-row">
              <span className="space-home-metrics-value">
                {formatSpaceMetricValue(profile.subscriptionCount ?? 0)}
              </span>
            </div>
            <span className="space-home-metrics-label">订阅</span>
          </div>
        </div>
        <div className="space-home-row space-home-row--actions">
          {isLoggedIn ? (
            favorStatus === 1 ? (
              <Button
                type="default"
                className="space-home-favor-btn"
                style={{ width: followButton.width }}
                onClick={onRequestUnfollow}
              >
                已关注
              </Button>
            ) : (
              <Button
                type="primary"
                className="space-home-favor-btn"
                style={{ width: followButton.width }}
                onClick={onFollow}
              >
                关注
              </Button>
            )
          ) : (
            <Button
              type="primary"
              className="space-home-favor-btn"
              style={{ width: followButton.width }}
              onClick={onLoginRequest}
            >
              关注
            </Button>
          )}
          <Button
            type="primary"
            className="space-home-subscribe-btn"
            style={{ width: subscribeButton.width }}
            onClick={onSubscribe}
          >
            订阅
          </Button>
        </div>
      </div>
    </div>
  );
}
